// Local admin visual language. Page data and actions stay with each route.
export const AdminPageHeader = {
  extends: 'PageHeader',
  padding: 'A B',
  LeftGroup: {
    Emblem: {
      hide: (el, s) => el.call('paneTitleBarShown', s),
      boxSize: 'controlH',
      flexShrink: '0',
      round: 'radiusPill',
      background: 'title',
      align: 'center center',
      ariaHidden: 'true',
      Icon: {
        name: (el) => el.lookup('Header')?.adminIcon || 'settings',
        boxSize: 'A1',
        color: 'title-reversed'
      }
    },
    Title: {
      fontSize: 'C',
      lineHeight: '1.2',
      '@mobileL': { fontSize: 'B' },
      '@appMobile': { fontSize: 'B' }
    }
  },
  '@mobileL': { padding: 'A' },
  '@appMobile': { padding: 'A' }
}

export const AdminPageBody = {
  flow: 'y',
  gap: 'A',
  padding: 'Z B B',
  overflow: 'auto',
  minWidth: '0',
  minHeight: '0',
  maxWidth: '100%',
  boxSizing: 'border-box',
  childProps: { flexShrink: '0' },
  '@mobileL': { padding: 'Z A A' },
  '@appMobile': { padding: 'Z A A' }
}

// Intrinsic columns also collapse inside a narrow PageSlot on a wide screen.
export const AdminSettingsBody = {
  extends: 'AdminPageBody',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-H)), 1fr))',
  alignItems: 'start',
  alignContent: 'start',
  width: '100%',
  maxWidth: 'J'
}

export const AdminMetricGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-G)), 1fr))',
  gap: 'Z',
  minWidth: '0',
  width: '100%',
  maxWidth: 'J',
  gridColumn: '1 / -1',
  childExtends: 'AdminMetricCard',
  childrenAs: 'state'
}

export const AdminMetricCard = {
  extends: 'AdminCard',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  childProps: { gridColumn: '1 / -1' },
  gap: 'Z',
  Header: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    Label: {
      fontSize: 'Z1',
      fontWeight: '500',
      color: 'caption',
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Icon: {
      name: (el, s) => s.icon || 'analytics',
      boxSize: 'B',
      padding: 'Y',
      boxSizing: 'border-box',
      round: 'radiusPill',
      background: 'widgetPlate',
      color: 'title',
      ariaHidden: 'true'
    }
  },
  Value: {
    gridColumn: '1',
    gridRow: '2',
    fontSize: 'E',
    fontWeight: '700',
    lineHeight: '1.1',
    color: 'title',
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere',
    text: (el, s) => s.value ?? '—'
  },
  Gauge: {
    hide: (el, s) => !(Number(s.limit) > 0) || s.value == null,
    gridColumn: '2',
    gridRow: '2',
    boxSize: 'D',
    padding: 'Y',
    boxSizing: 'border-box',
    round: 'radiusPill',
    ariaHidden: 'true',
    background: (el, s) => {
      const percent = Math.max(0, Math.min(100, (Number(s.value) / Number(s.limit)) * 100)) || 0
      return `conic-gradient(var(--color-title) ${percent * 3.6}deg, var(--color-widgetPlate) 0deg)`
    },
    Center: {
      width: '100%',
      height: '100%',
      background: 'cardSolid',
      round: 'radiusPill',
      align: 'center center',
      fontSize: 'Z',
      fontWeight: '700',
      color: 'title',
      text: (el, s) =>
        `${Math.round(Math.max(0, Math.min(100, (Number(s.value) / Number(s.limit)) * 100)) || 0)}%`
    }
  },
  ProgressBar: {
    hide: (el, s) => !(Number(s.limit) > 0) || s.value == null,
    height: 'Y',
    background: 'widgetPlate',
    ariaLabel: (el, s) => el.call('polyglot', s.label || ''),
    role: 'progressbar',
    value: (el, s) => Math.max(0, Math.min(1, Number(s.value) / Number(s.limit))) || 0,
    'aria-valuemin': '0',
    'aria-valuemax': (el, s) => String(Number(s.limit) || 0),
    'aria-valuenow': (el, s) => String(Math.max(0, Math.min(Number(s.limit), Number(s.value)))),
    Fill: {
      width: (el, s) =>
        `${Math.max(0, Math.min(100, (Number(s.value) / Number(s.limit)) * 100)) || 0}%`,
      background: 'title'
    }
  }
}

export const AdminPricingCard = {
  flow: 'y',
  gap: 'A',
  minWidth: '0',
  padding: 'A',
  round: 'radiusCard',
  background: 'raised',
  isCurrent: (el, s) => !!s._isCurrent,
  '.isCurrent': { shadow: 'float' },
  Identity: {
    flow: 'x',
    align: 'center space-between',
    Mark: {
      tag: 'div',
      boxSize: 'C',
      round: 'radiusPill',
      background: (el, s) => (s._isCurrent ? 'title' : 'widgetPlate'),
      align: 'center center',
      ariaHidden: 'true',
      Icon: {
        name: (el, s) => (s._isCurrent ? 'check' : s.salesLed ? 'network' : 'layers'),
        boxSize: 'A1',
        color: (el, s) => (s._isCurrent ? 'title-reversed' : 'title')
      }
    },
    AppStatusChip: {
      hide: (el, s) => !s._isCurrent,
      label: 'admin.usage.tiers.current'
    }
  },
  Top: {
    Name: {
      tag: 'h3',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      lineHeight: '1.2'
    }
  },
  Price: {
    fontSize: 'D',
    fontWeight: '700',
    color: 'title-reversed',
    background: 'title',
    padding: 'A',
    round: 'radiusControl',
    lineHeight: '1.1',
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere'
  },
  Billing: {
    fontSize: 'Y2',
    color: 'caption',
    text: '{{ admin.usage.tiers.billing | polyglot }}'
  },
  Credits: {
    fontSize: 'Z1',
    fontWeight: '500',
    color: 'title',
    padding: '0 0 Z',
    borderBottom: 'hairline solid line',
    fontVariantNumeric: 'tabular-nums'
  },
  Perks: {
    tag: 'ul',
    flex: '1',
    flow: 'y',
    gap: 'Z1',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    childExtends: 'AdminPlanBenefit',
    childrenAs: 'state'
  },
  Current: {
    hide: (el, s) => !s._isCurrent,
    minHeight: 'controlH',
    align: 'center center',
    gap: 'Y',
    background: 'selected',
    round: 'radiusControl',
    color: 'title',
    fontSize: 'Z2',
    fontWeight: '500',
    Icon: { name: 'check', boxSize: 'Z1', ariaHidden: 'true' },
    Label: { text: '{{ admin.usage.tiers.current | polyglot }}' }
  }
}

export const AdminPlanBenefit = {
  tag: 'li',
  flow: 'x',
  align: 'start',
  gap: 'Y',
  Icon: { name: 'check', boxSize: 'Z1', color: 'title', flexShrink: '0', ariaHidden: 'true' },
  Label: { fontSize: 'Z', color: 'caption', lineHeight: '1.5', text: (el, s) => s.value }
}
